import React, { useState, useEffect } from 'react';
import { channelsAPI, microsoftAPI, authAPI } from '../services/api';
import Skeleton from '../components/common/Skeleton';
import ErrorState from '../components/common/ErrorState';
import ButtonSpinner from '../components/common/ButtonSpinner';

const Channels = () => {
    // State for each channel
    const [gmailStatus, setGmailStatus] = useState(null);
    const [twilioStatus, setTwilioStatus] = useState(null);
    const [whatsappStatus, setWhatsappStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testingGmail, setTestingGmail] = useState(false);
    const [actionMessage, setActionMessage] = useState(null);
    const [pageError, setPageError] = useState(null);
    const [gmailConnecting, setGmailConnecting] = useState(false);
    const [gmailDisconnecting, setGmailDisconnecting] = useState(false);
    const [showUsageModal, setShowUsageModal] = useState(false);
    const [microsoftStatus, setMicrosoftStatus] = useState(null);
    const [microsoftConnecting, setMicrosoftConnecting] = useState(false);
    const [microsoftTesting, setMicrosoftTesting] = useState(false);
    const [isTrial, setIsTrial] = useState(false);

    // IMAP state
    const [imapStatus, setImapStatus] = useState(null);
    const [imapConnecting, setImapConnecting] = useState(false);
    const [imapTesting, setImapTesting] = useState(false);
    const [emailForm, setEmailForm] = useState({ email: '', password: '', provider: 'auto', customHost: '', customPort: '' });

    const [showLimitModal, setShowLimitModal] = useState(false);
    const [limitMessage, setLimitMessage] = useState('');

    // Fetch all channel statuses on mount
    useEffect(() => {
        fetchAllStatuses();

        // Check for Microsoft OAuth success or error
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'microsoft') {
            setActionMessage({ type: 'success', text: 'Microsoft account connected successfully!' });
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // fetchAllStatuses will run anyway due to mount, but let's ensure fresh data
            setTimeout(fetchAllStatuses, 500);
        } else if (params.get('error') === 'microsoft_failed') {
            const msg = params.get('message');
            setActionMessage({
                type: 'error',
                text: msg ? `Connection failed: ${decodeURIComponent(msg)}` : 'Microsoft connection failed.'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Check for Gmail OAuth success or error
        if (params.get('success') === 'gmail') {
            setActionMessage({ type: 'success', text: 'Gmail account connected successfully!' });
            window.history.replaceState({}, document.title, window.location.pathname);
            setTimeout(fetchAllStatuses, 500);
        } else if (params.get('error') === 'gmail_failed') {
            const msg = params.get('message');
            setActionMessage({
                type: 'error',
                text: msg ? `Connection failed: ${decodeURIComponent(msg)}` : 'Gmail connection failed.'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // Auto-dismiss action messages
    useEffect(() => {
        if (actionMessage) {
            const timer = setTimeout(() => setActionMessage(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [actionMessage]);

    const fetchAllStatuses = async () => {
        try {
            setLoading(true);
            setPageError(null);
            const [gmailRes, msRes, twilioRes, whatsappRes, imapRes, userRes] = await Promise.allSettled([
                channelsAPI.getGmailStatus(),
                microsoftAPI.getStatus(),
                channelsAPI.getTwilioStatus(),
                channelsAPI.getWhatsAppStatus(),
                channelsAPI.getIMAPStatus(),
                authAPI.getCurrentUser()
            ]);

            setGmailStatus(gmailRes.status === 'fulfilled' ? gmailRes.value.data : { connected: false });
            setMicrosoftStatus(msRes.status === 'fulfilled' ? msRes.value.data : { connected: false });
            setTwilioStatus(twilioRes.status === 'fulfilled' ? twilioRes.value.data : { connected: false });
            setWhatsappStatus(whatsappRes.status === 'fulfilled' ? whatsappRes.value.data : { connected: false });
            setImapStatus(imapRes.status === 'fulfilled' ? imapRes.value.data : { connected: false });

            if (userRes.status === 'fulfilled' && userRes.value.data) {
                setIsTrial(userRes.value.data.subscription_status === 'trial');
            }
        } catch (error) {
            console.error('Error fetching channel statuses:', error);
            setPageError(error.message || 'Failed to load channel configurations.');
        } finally {
            setLoading(false);
        }
    };

    // --- Gmail Handlers ---
    const handleConnectGmail = async () => {
        setGmailConnecting(true);
        try {
            const res = await channelsAPI.connectGmail();
            if (res.data?.authUrl) {
                window.location.href = res.data.authUrl;
            } else {
                console.error('No authUrl in response:', res.data);
                setActionMessage({ type: 'error', text: 'No authorization URL received from server.' });
                setGmailConnecting(false);
            }
        } catch (err) {
            console.error('Error connecting Gmail:', err?.response?.data || err.message);

            // Handle LIMIT_REACHED
            if (err?.response?.data?.code === 'LIMIT_REACHED') {
                setLimitMessage(err.response.data.message || 'Plan limit reached.');
                setShowLimitModal(true);
                setGmailConnecting(false);
                return;
            }

            const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to initiate Gmail connection.';
            setActionMessage({ type: 'error', text: msg });
            setGmailConnecting(false);
        }
    };

    const handleDisconnectGmail = async () => {
        if (!window.confirm('Disconnect Gmail? You will stop receiving leads from email.')) return;
        try {
            setGmailDisconnecting(true);
            await channelsAPI.disconnectGmail();
            setGmailStatus({ connected: false });
            setActionMessage({ type: 'success', text: 'Gmail disconnected successfully.' });
        } catch (err) {
            console.error('Error disconnecting Gmail:', err);
            setActionMessage({ type: 'error', text: 'Failed to disconnect Gmail.' });
        } finally {
            setGmailDisconnecting(false);
        }
    };

    const handleTestGmail = async () => {
        setTestingGmail(true);
        try {
            const res = await channelsAPI.testGmailConnection();

            // If test reveals token is expired, update local state
            if (res.data?.token_expired) {
                setGmailStatus(prev => ({ ...prev, token_expired: true, is_enabled: false }));
            }

            setActionMessage({
                type: res.data?.success ? 'success' : 'error',
                text: res.data?.message || 'Test complete.',
            });
        } catch (err) {
            setActionMessage({ type: 'error', text: 'Connection test failed.' });
        } finally {
            setTestingGmail(false);
        }
    };

    // --- Microsoft Handlers ---
    const handleConnectMicrosoft = async () => {
        setMicrosoftConnecting(true);
        try {
            const res = await microsoftAPI.startAuth();
            if (res.data?.authUrl) {
                window.location.href = res.data.authUrl;
            } else {
                setActionMessage({ type: 'error', text: 'No auth URL received.' });
                setMicrosoftConnecting(false);
            }
        } catch (err) {
            console.error('Error connecting Microsoft:', err);

            // Handle LIMIT_REACHED
            if (err?.response?.data?.code === 'LIMIT_REACHED') {
                setLimitMessage(err.response.data.details || err.response.data.error || 'Plan limit reached.');
                setShowLimitModal(true);
                setMicrosoftConnecting(false);
                return;
            }

            setActionMessage({ type: 'error', text: 'Failed to initiate Microsoft connection.' });
            setMicrosoftConnecting(false);
        }
    };

    const handleDisconnectMicrosoft = async () => {
        if (!window.confirm('Are you sure you want to disconnect your Microsoft account?')) return;

        try {
            setMicrosoftConnecting(true);
            await microsoftAPI.disconnect();
            setMicrosoftStatus({ connected: false });
            setActionMessage({ type: 'success', text: 'Microsoft account disconnected successfully' });
        } catch (error) {
            console.error('Error disconnecting Microsoft:', error);
            setActionMessage({ type: 'error', text: 'Failed to disconnect Microsoft account' });
        } finally {
            setMicrosoftConnecting(false);
        }
    };

    const handleTestMicrosoftConnection = async () => {
        try {
            setMicrosoftTesting(true);
            const { data } = await microsoftAPI.testConnection();

            // If test reveals token is expired, update local state
            if (data.token_expired) {
                setMicrosoftStatus(prev => ({ ...prev, token_expired: true, is_enabled: false }));
            }

            if (data.success) {
                setActionMessage({ type: 'success', text: `Connection healthy: ${data.message} (${data.email})` });
            } else {
                setActionMessage({ type: 'error', text: data.message || 'Connection test failed' });
            }
        } catch (error) {
            console.error('Error testing Microsoft connection:', error);
            setActionMessage({ type: 'error', text: 'Failed to test connection' });
        } finally {
            setMicrosoftTesting(false);
        }
    };
    // --- Utility ---
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    // --- IMAP Handlers ---
    const handleConnectIMAP = async () => {
        if (!emailForm.email || !emailForm.password) {
            setActionMessage({ type: 'error', text: 'Please enter your email and password.' });
            return;
        }
        setImapConnecting(true);
        try {
            const res = await channelsAPI.connectIMAP({
                email: emailForm.email,
                password: emailForm.password,
                provider: emailForm.provider,
                customHost: emailForm.customHost,
                customPort: emailForm.customPort,
            });
            if (res.data?.success) {
                setActionMessage({ type: 'success', text: `Email connected via IMAP (${res.data.provider})` });
                setEmailForm({ email: '', password: '', provider: 'auto', customHost: '', customPort: '' });
                fetchAllStatuses();
            }
        } catch (err) {
            console.error('Error connecting IMAP:', err?.response?.data || err.message);

            // Handle LIMIT_REACHED
            if (err?.response?.data?.code === 'LIMIT_REACHED') {
                setLimitMessage(err.response.data.message || err.response.data.error || 'Plan limit reached.');
                setShowLimitModal(true);
                setImapConnecting(false);
                return;
            }

            const msg = err?.response?.data?.error || err?.response?.data?.details || 'Failed to connect.';
            setActionMessage({ type: 'error', text: msg });
        } finally {
            setImapConnecting(false);
        }
    };

    const handleDisconnectIMAP = async () => {
        if (!window.confirm('Disconnect IMAP email? You will stop receiving leads from this email.')) return;
        try {
            await channelsAPI.disconnectIMAP();
            setImapStatus({ connected: false });
            setActionMessage({ type: 'success', text: 'IMAP email disconnected.' });
        } catch (err) {
            setActionMessage({ type: 'error', text: 'Failed to disconnect IMAP email.' });
        }
    };

    const handleTestIMAP = async () => {
        setImapTesting(true);
        try {
            const res = await channelsAPI.testIMAPConnection();
            setActionMessage({
                type: res.data?.success ? 'success' : 'error',
                text: res.data?.message || 'Test complete.',
            });
        } catch (err) {
            setActionMessage({ type: 'error', text: 'IMAP connection test failed.' });
        } finally {
            setImapTesting(false);
        }
    };

    const usagePercent = twilioStatus?.connected
        ? Math.min(Math.round((twilioStatus.monthlyUsage / (twilioStatus.monthlyLimit || 1000)) * 100), 100)
        : 0;

    // --- Loading Skeletons ---
    if (loading) {
        return (
            <div className="bg-mint dark:bg-navy min-h-screen p-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div className="w-1/3">
                        <Skeleton.Text lines={2} />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => <Skeleton.Card key={i} />)}
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="bg-mint dark:bg-navy min-h-screen p-6 flex justify-center items-center">
                <ErrorState message={pageError} onRetry={fetchAllStatuses} className="w-full max-w-lg" />
            </div>
        );
    }

    return (
        <div className="bg-mint dark:bg-navy min-h-screen scroll-smooth">
            {/* Toast */}
            {actionMessage && (
                <div className={`fixed top-20 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-bold flex items-center gap-2 ${actionMessage.type === 'success'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                    <span className="material-symbols-outlined text-lg">
                        {actionMessage.type === 'success' ? 'check_circle' : 'error'}
                    </span>
                    {actionMessage.text}
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-darkTeal dark:text-light mb-1 text-left">Channels</h2>
                    <p className="text-darkTeal/60 dark:text-light/60 text-sm text-left">Manage your connected communication channels</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={fetchAllStatuses}
                        className="px-4 py-2 bg-white dark:bg-navy border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/5 dark:hover:bg-cyan/10 font-medium rounded-lg transition-colors flex items-center gap-2 text-sm"
                    >
                        <span className="material-symbols-outlined text-base">refresh</span>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Channel Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

                {/* ─── CARD: Email (IMAP) ─── */}
                <div className="bg-white dark:bg-navy rounded-xl border border-primary/30 dark:border-cyan shadow-sm dark:shadow-[0_4px_20px_rgba(8,217,214,0.05)] hover:shadow-lg dark:hover:shadow-[0_4px_30px_rgba(8,217,214,0.15)] transition-all group p-6 flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-bl-full pointer-events-none"></div>

                    <div className="flex justify-between items-start mb-6">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500/20 to-mint dark:to-navy border border-emerald-500/30 flex items-center justify-center shadow-lg">
                            <span className="material-symbols-outlined text-2xl text-emerald-500 dark:text-emerald-400">alternate_email</span>
                        </div>
                        {imapStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 dark:text-green-400 border border-green-500/20 text-xs font-bold uppercase tracking-wider">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                Connected
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 text-xs font-bold uppercase tracking-wider">
                                Not Connected
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-darkTeal dark:text-light text-left">Email (IMAP)</h3>
                        <span className="px-2 py-0.5 rounded text-[10px] font-black bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 uppercase tracking-wider flex items-center gap-1">
                            <span className="material-symbols-outlined text-[12px]">star</span>
                            BEST
                        </span>
                    </div>
                    <p className="text-darkTeal/50 dark:text-light/50 text-sm mb-5 font-mono text-left">
                        {imapStatus?.email || 'Any email provider via IMAP'}
                    </p>

                    {imapStatus?.connected ? (
                        <>
                            <div className="space-y-3 mb-6 flex-1">
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Provider</span>
                                    <span className="text-darkTeal dark:text-light font-medium capitalize">{imapStatus.provider}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Connected</span>
                                    <span className="text-darkTeal dark:text-light font-medium">
                                        {formatDate(imapStatus.connected_at)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-darkTeal/60 dark:text-light/60">Status</span>
                                    <span className="text-green-500 dark:text-green-400 font-medium">
                                        {imapStatus.is_enabled ? 'Active' : 'Disabled'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-auto">
                                <button
                                    onClick={handleTestIMAP}
                                    disabled={imapTesting}
                                    className="flex-1 py-2 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 text-darkTeal dark:text-light border border-teal/20 dark:border-light/10 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    {imapTesting ? <ButtonSpinner size={18} /> : 'Test Connection'}
                                </button>
                                <button
                                    onClick={handleDisconnectIMAP}
                                    className="flex-1 py-2 rounded-lg bg-transparent hover:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/30 hover:border-red-500 text-sm font-medium transition-colors flex justify-center items-center gap-1"
                                >
                                    Disconnect
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col">
                            <div className="space-y-3 mb-4">
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={emailForm.email}
                                    onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                                    className="w-full px-4 py-2.5 bg-mint dark:bg-light/5 border border-teal/20 dark:border-light/10 rounded-lg text-darkTeal dark:text-light placeholder-darkTeal/40 dark:placeholder-light/30 text-sm focus:outline-none focus:border-primary dark:focus:border-cyan transition-colors"
                                />
                                <input
                                    type="password"
                                    placeholder="App Password"
                                    value={emailForm.password}
                                    onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                                    className="w-full px-4 py-2.5 bg-mint dark:bg-light/5 border border-teal/20 dark:border-light/10 rounded-lg text-darkTeal dark:text-light placeholder-darkTeal/40 dark:placeholder-light/30 text-sm focus:outline-none focus:border-primary dark:focus:border-cyan transition-colors"
                                />
                                <select
                                    value={emailForm.provider}
                                    onChange={(e) => setEmailForm({ ...emailForm, provider: e.target.value })}
                                    className="w-full px-4 py-2.5 bg-mint dark:bg-navy border border-teal/20 dark:border-light/10 rounded-lg text-darkTeal dark:text-light text-sm focus:outline-none focus:border-primary dark:focus:border-cyan transition-colors cursor-pointer"
                                >
                                    <option className="bg-white dark:bg-navy text-darkTeal dark:text-light" value="auto">Auto-detect provider</option>
                                    <option className="bg-white dark:bg-navy text-darkTeal dark:text-light" value="gmail">Gmail</option>
                                    <option className="bg-white dark:bg-navy text-darkTeal dark:text-light" value="outlook">Outlook / Office 365</option>
                                    <option className="bg-white dark:bg-navy text-darkTeal dark:text-light" value="yahoo">Yahoo Mail</option>
                                    <option className="bg-white dark:bg-navy text-darkTeal dark:text-light" value="icloud">iCloud Mail</option>
                                    <option className="bg-white dark:bg-navy text-darkTeal dark:text-light" value="custom">Other (custom IMAP)</option>
                                </select>

                                {emailForm.provider === 'custom' && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <input
                                            placeholder="IMAP server"
                                            value={emailForm.customHost}
                                            onChange={(e) => setEmailForm({ ...emailForm, customHost: e.target.value })}
                                            className="px-4 py-2.5 bg-mint dark:bg-light/5 border border-teal/20 dark:border-light/10 rounded-lg text-darkTeal dark:text-light placeholder-darkTeal/40 dark:placeholder-light/30 text-sm focus:outline-none focus:border-primary dark:focus:border-cyan transition-colors"
                                        />
                                        <input
                                            placeholder="Port (993)"
                                            value={emailForm.customPort}
                                            onChange={(e) => setEmailForm({ ...emailForm, customPort: e.target.value })}
                                            className="px-4 py-2.5 bg-mint dark:bg-light/5 border border-teal/20 dark:border-light/10 rounded-lg text-darkTeal dark:text-light placeholder-darkTeal/40 dark:placeholder-light/30 text-sm focus:outline-none focus:border-primary dark:focus:border-cyan transition-colors"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Gmail App Password Guide */}
                            <div className="bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
                                <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold mb-1.5">For Gmail users:</p>
                                <ol className="text-[11px] text-blue-500/80 dark:text-blue-300/70 space-y-0.5 list-decimal list-inside">
                                    <li>Enable 2-Step Verification in Google Account</li>
                                    <li>Go to Security → App passwords</li>
                                    <li>Generate an app password and use it here</li>
                                </ol>
                                <a
                                    href="https://myaccount.google.com/apppasswords"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] text-blue-500 dark:text-blue-400 hover:underline mt-1.5 inline-flex items-center gap-1"
                                >
                                    Create app password
                                    <span className="material-symbols-outlined text-xs">open_in_new</span>
                                </a>
                            </div>

                            <button
                                onClick={handleConnectIMAP}
                                disabled={imapConnecting}
                                className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg shadow-emerald-500/20 text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-auto"
                            >
                                {imapConnecting ? <ButtonSpinner size={18} className="text-white" /> : <span className="material-symbols-outlined text-base">link</span>}
                                {imapConnecting ? 'Testing connection...' : 'Connect Email'}
                            </button>
                        </div>
                    )}
                </div>

                {/* ─── CARD 2: Microsoft/Outlook ─── */}
                <div className="bg-white dark:bg-navy rounded-xl border border-primary/30 dark:border-cyan shadow-sm dark:shadow-[0_4px_20px_rgba(8,217,214,0.05)] hover:shadow-lg dark:hover:shadow-[0_4px_30px_rgba(8,217,214,0.15)] transition-all group p-6 flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full pointer-events-none"></div>

                    <div className="flex justify-between items-start mb-6">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500/20 to-mint dark:to-navy border border-blue-500/30 flex items-center justify-center shadow-lg">
                            <span className="text-2xl">📧</span>
                        </div>
                        {microsoftStatus?.connected && microsoftStatus?.token_expired ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 dark:text-amber-400 border border-amber-500/20 text-xs font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-sm">warning</span>
                                Token Expired
                            </span>
                        ) : microsoftStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 dark:text-green-400 border border-green-500/20 text-xs font-bold uppercase tracking-wider">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                Connected
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 text-xs font-bold uppercase tracking-wider">
                                Not Connected
                            </span>
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-darkTeal dark:text-light mb-1 text-left">Microsoft/Outlook</h3>
                    <p className="text-darkTeal/50 dark:text-light/50 text-sm mb-5 font-mono text-left">
                        {microsoftStatus?.email || 'No account linked'}
                    </p>

                    {microsoftStatus?.connected && microsoftStatus?.token_expired ? (
                        /* Microsoft Token Expired State */
                        <>
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-5">
                                <div className="flex items-start gap-3">
                                    <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">error</span>
                                    <div>
                                        <p className="text-sm font-bold text-amber-600 dark:text-amber-400 mb-1">Token Expired</p>
                                        <p className="text-xs text-darkTeal/60 dark:text-light/50">
                                            Your Outlook access token has expired. Lead monitoring is paused. Please reconnect to resume.
                                        </p>
                                        {microsoftStatus.token_expired_at && (
                                            <p className="text-[11px] text-darkTeal/40 dark:text-light/30 mt-1 font-mono">
                                                Expired: {formatDate(microsoftStatus.token_expired_at)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-auto">
                                <button
                                    onClick={handleConnectMicrosoft}
                                    disabled={microsoftConnecting}
                                    className="flex-1 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-lg shadow-amber-500/20 text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {microsoftConnecting ? <ButtonSpinner size={18} className="text-white" /> : <span className="material-symbols-outlined text-base">link</span>}
                                    {microsoftConnecting ? 'Reconnecting...' : 'Reconnect Outlook'}
                                </button>
                                <button
                                    onClick={handleDisconnectMicrosoft}
                                    disabled={microsoftConnecting}
                                    className="py-2 px-4 rounded-lg bg-transparent hover:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/30 hover:border-red-500 text-sm font-medium transition-colors disabled:opacity-50 flex justify-center items-center gap-1"
                                >
                                    Disconnect
                                </button>
                            </div>
                        </>
                    ) : microsoftStatus?.connected ? (
                        <>
                            <div className="space-y-3 mb-6 flex-1">
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Connected</span>
                                    <span className="text-darkTeal dark:text-light font-medium">
                                        {formatDate(microsoftStatus.connected_at)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Status</span>
                                    <span className="text-green-500 dark:text-green-400 font-medium">Active</span>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-auto">
                                <button
                                    onClick={handleTestMicrosoftConnection}
                                    disabled={microsoftTesting}
                                    className="flex-1 py-2 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 text-darkTeal dark:text-light border border-teal/20 dark:border-light/10 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    {microsoftTesting ? <ButtonSpinner size={18} /> : 'Test Connection'}
                                </button>
                                <button
                                    onClick={handleDisconnectMicrosoft}
                                    disabled={microsoftConnecting}
                                    className="flex-1 py-2 rounded-lg bg-transparent hover:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/30 hover:border-red-500 text-sm font-medium transition-colors disabled:opacity-50 flex justify-center items-center gap-1"
                                >
                                    {microsoftConnecting ? <ButtonSpinner size={18} className="text-red-500" /> : 'Disconnect'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col">
                            <div className="bg-mint dark:bg-light/5 border border-teal/10 dark:border-light/10 rounded-lg p-4 mb-5 flex-1">
                                <p className="text-sm text-darkTeal/70 dark:text-light/60 mb-3 font-semibold text-left">Quick Setup:</p>
                                <ol className="list-decimal list-inside space-y-2 text-sm text-darkTeal/60 dark:text-light/50 text-left">
                                    <li>Click "Connect Outlook" below</li>
                                    <li>Authorize Microsoft access</li>
                                    <li>We'll monitor emails for leads</li>
                                </ol>
                            </div>
                            <button
                                onClick={handleConnectMicrosoft}
                                disabled={microsoftConnecting}
                                className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-500/20 text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {microsoftConnecting ? <ButtonSpinner size={18} className="text-white dark:text-navy" /> : <span className="material-symbols-outlined text-base">link</span>}
                                {microsoftConnecting ? 'Connecting...' : 'Connect Outlook'}
                            </button>
                        </div>
                    )}
                </div>

                {/* ─── CARD 1: Gmail ─── */}
                <div className="bg-white dark:bg-navy rounded-xl border border-primary/30 dark:border-cyan shadow-sm dark:shadow-[0_4px_20px_rgba(8,217,214,0.05)] hover:shadow-lg dark:hover:shadow-[0_4px_30px_rgba(8,217,214,0.15)] transition-all group p-6 flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/10 dark:from-cyan/10 to-transparent rounded-bl-full pointer-events-none"></div>

                    <div className="flex justify-between items-start mb-6">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 dark:from-cyan/20 to-mint dark:to-navy border border-primary/30 dark:border-cyan/30 flex items-center justify-center shadow-lg">
                            <span className="material-symbols-outlined text-2xl text-primary dark:text-cyan">mail</span>
                        </div>
                        {gmailStatus?.connected && gmailStatus?.token_expired ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 dark:text-amber-400 border border-amber-500/20 text-xs font-bold uppercase tracking-wider">
                                <span className="material-symbols-outlined text-sm">warning</span>
                                Token Expired
                            </span>
                        ) : gmailStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 dark:text-green-400 border border-green-500/20 text-xs font-bold uppercase tracking-wider">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                Connected
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 text-xs font-bold uppercase tracking-wider">
                                Not Connected
                            </span>
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-darkTeal dark:text-light mb-1 text-left">Gmail Integration</h3>
                    <p className="text-darkTeal/50 dark:text-light/50 text-sm mb-5 font-mono text-left">
                        {gmailStatus?.email || 'No account linked'}
                    </p>

                    {gmailStatus?.connected && gmailStatus?.token_expired ? (
                        /* Token Expired State */
                        <>
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-5">
                                <div className="flex items-start gap-3">
                                    <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">error</span>
                                    <div>
                                        <p className="text-sm font-bold text-amber-600 dark:text-amber-400 mb-1">Token Expired</p>
                                        <p className="text-xs text-darkTeal/60 dark:text-light/50">
                                            Your Gmail access token has expired. Lead monitoring is paused. Please reconnect to resume.
                                        </p>
                                        {gmailStatus.token_expired_at && (
                                            <p className="text-[11px] text-darkTeal/40 dark:text-light/30 mt-1 font-mono">
                                                Expired: {formatDate(gmailStatus.token_expired_at)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-auto">
                                <button
                                    onClick={handleConnectGmail}
                                    disabled={gmailConnecting}
                                    className="flex-1 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-lg shadow-amber-500/20 text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {gmailConnecting ? <ButtonSpinner size={18} className="text-white" /> : <span className="material-symbols-outlined text-base">link</span>}
                                    {gmailConnecting ? 'Reconnecting...' : 'Reconnect Gmail'}
                                </button>
                                <button
                                    onClick={handleDisconnectGmail}
                                    disabled={gmailDisconnecting}
                                    className="py-2 px-4 rounded-lg bg-transparent hover:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/30 hover:border-red-500 text-sm font-medium transition-colors disabled:opacity-50 flex justify-center items-center gap-1"
                                >
                                    {gmailDisconnecting ? <ButtonSpinner size={18} className="text-red-500" /> : 'Disconnect'}
                                </button>
                            </div>
                        </>
                    ) : gmailStatus?.connected ? (
                        <>
                            <div className="space-y-3 mb-6 flex-1">
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Connected</span>
                                    <span className="text-darkTeal dark:text-light font-medium">
                                        {formatDate(gmailStatus.connected_at || gmailStatus.verified_at)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Status</span>
                                    <span className={`font-medium ${gmailStatus.is_enabled ? 'text-green-500 dark:text-green-400' : 'text-orange-500'}`}>
                                        {gmailStatus.is_enabled ? 'Active' : 'Disabled'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-auto">
                                <button
                                    onClick={handleTestGmail}
                                    disabled={testingGmail}
                                    className="flex-1 py-2 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 text-darkTeal dark:text-light border border-teal/20 dark:border-light/10 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    {testingGmail ? <ButtonSpinner size={18} /> : 'Test Connection'}
                                </button>
                                <button
                                    onClick={handleDisconnectGmail}
                                    disabled={gmailDisconnecting}
                                    className="flex-1 py-2 rounded-lg bg-transparent hover:bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/30 hover:border-red-500 text-sm font-medium transition-colors disabled:opacity-50 flex justify-center items-center gap-1"
                                >
                                    {gmailDisconnecting ? <ButtonSpinner size={18} className="text-red-500" /> : 'Disconnect'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col">
                            <div className="bg-mint dark:bg-light/5 border border-teal/10 dark:border-light/10 rounded-lg p-4 mb-4 flex-1">
                                <p className="text-sm text-darkTeal/70 dark:text-light/60 mb-3 font-semibold text-left">Quick Setup:</p>
                                <ol className="list-decimal list-inside space-y-2 text-sm text-darkTeal/60 dark:text-light/50 text-left">
                                    <li>Click "Connect Gmail" below</li>
                                    <li>Authorize OffTrigger to access your email</li>
                                    <li>We'll start monitoring for leads automatically</li>
                                </ol>
                            </div>

                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4 flex items-start gap-2 text-left">
                                <span className="material-symbols-outlined text-amber-500 text-[16px] mt-0.5">info</span>
                                <p className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed">
                                    <strong>Note:</strong> You may see an "unverified app" warning from Google since we are under Verification. This is normal, just click <strong>Advanced &rarr; Proceed</strong> to connect safely.
                                </p>
                            </div>

                            <button
                                onClick={handleConnectGmail}
                                disabled={gmailConnecting}
                                className="w-full py-2.5 rounded-lg bg-primary dark:bg-cyan hover:bg-primary/80 dark:hover:bg-cyan/80 text-white dark:text-navy font-bold shadow-lg shadow-primary/20 dark:shadow-cyan/20 text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {gmailConnecting ? <ButtonSpinner size={18} className="text-white dark:text-navy" /> : <span className="material-symbols-outlined text-base">link</span>}
                                {gmailConnecting ? 'Connecting...' : 'Connect Gmail'}
                            </button>
                        </div>
                    )}
                </div>

                {/* ─── CARD 3: SMS (Twilio) ─── */}
                <div className="bg-white dark:bg-navy rounded-xl border border-primary/30 dark:border-cyan shadow-sm dark:shadow-[0_4px_20px_rgba(8,217,214,0.05)] hover:shadow-lg dark:hover:shadow-[0_4px_30px_rgba(8,217,214,0.15)] transition-all group p-6 flex flex-col relative overflow-hidden">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500/20 to-mint dark:to-navy border border-indigo-500/30 flex items-center justify-center shadow-lg">
                            <span className="material-symbols-outlined text-2xl text-indigo-500 dark:text-indigo-400">smartphone</span>
                        </div>
                        {twilioStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 dark:text-green-400 border border-green-500/20 text-xs font-bold uppercase tracking-wider">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                Connected
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 text-xs font-bold uppercase tracking-wider">
                                Not Connected
                            </span>
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-darkTeal dark:text-light mb-1 text-left">SMS (Twilio)</h3>
                    <div className="mb-5 text-left">
                        <p className="text-darkTeal/50 dark:text-light/50 text-sm font-mono">
                            {twilioStatus?.phoneNumber || 'No number configured'}
                        </p>
                        {isTrial && (
                            <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400">
                                <span className="material-symbols-outlined text-sm">info</span>
                                <span className="text-xs font-bold uppercase tracking-wide">SMS Limited for Trial Users</span>
                            </div>
                        )}
                    </div>

                    {twilioStatus?.connected ? (
                        <>
                            <div className="space-y-4 mb-5 flex-1">
                                {/* Usage Progress Bar */}
                                <div>
                                    <div className="flex justify-between text-xs text-darkTeal/70 dark:text-light/70 mb-2">
                                        <span>Monthly Usage</span>
                                        <span className="font-bold text-primary dark:text-cyan">
                                            {twilioStatus.monthlyUsage?.toLocaleString()} / {(twilioStatus.monthlyLimit || 1000).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="h-2 w-full bg-teal/10 dark:bg-navy border border-teal/10 dark:border-light/10 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-primary dark:from-cyan to-blue-500 rounded-full transition-all duration-700 ease-out"
                                            style={{ width: `${usagePercent}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Stats Tiles */}
                                <div className="flex items-center gap-3 py-1">
                                    <div className="flex-1 bg-mint dark:bg-light/5 rounded-lg p-3 text-center border border-teal/10 dark:border-light/5">
                                        <p className="text-[10px] text-darkTeal/40 dark:text-light/40 uppercase tracking-wider font-bold">Response Rate</p>
                                        <p className="text-xl font-bold text-primary dark:text-cyan">{twilioStatus.responseRate || 0}%</p>
                                    </div>
                                    <div className="flex-1 bg-mint dark:bg-light/5 rounded-lg p-3 text-center border border-teal/10 dark:border-light/5">
                                        <p className="text-[10px] text-darkTeal/40 dark:text-light/40 uppercase tracking-wider font-bold">Delivered</p>
                                        <p className="text-xl font-bold text-indigo-500 dark:text-indigo-400">{twilioStatus.deliveredCount || 0}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-auto">
                                <button
                                    onClick={() => setShowUsageModal(true)}
                                    className="flex-1 py-2 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 text-darkTeal dark:text-light border border-teal/20 dark:border-light/10 text-sm font-medium transition-colors"
                                >
                                    View Usage
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col">
                            <div className="bg-mint dark:bg-light/5 border border-teal/10 dark:border-light/10 rounded-lg p-4 mb-5 flex-1">
                                <p className="text-sm text-darkTeal/70 dark:text-light/60 mb-3 font-semibold text-left">Setup Required:</p>
                                <ol className="list-decimal list-inside space-y-2 text-sm text-darkTeal/60 dark:text-light/50 text-left">
                                    <li>Add TWILIO_ACCOUNT_SID to your .env</li>
                                    <li>Add TWILIO_AUTH_TOKEN and TWILIO_PHONE_NUMBER</li>
                                    <li>Restart the backend server</li>
                                </ol>
                            </div>
                            <p className="text-xs text-center text-darkTeal/40 dark:text-light/30">
                                Configure your Twilio credentials in the backend .env file.
                            </p>
                        </div>
                    )}
                </div>

                {/* ─── CARD 3: WhatsApp ─── */}
                <div className="bg-white dark:bg-navy rounded-xl border border-primary/30 dark:border-cyan shadow-sm dark:shadow-[0_4px_20px_rgba(8,217,214,0.05)] hover:shadow-lg dark:hover:shadow-[0_4px_30px_rgba(8,217,214,0.15)] transition-all group p-6 flex flex-col relative overflow-hidden">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500/20 to-mint dark:to-navy border border-green-500/30 flex items-center justify-center shadow-lg">
                            <span className="material-symbols-outlined text-2xl text-green-500 dark:text-green-400">chat_bubble</span>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            {whatsappStatus?.connected ? (
                                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 dark:text-green-400 border border-green-500/20 text-xs font-bold uppercase tracking-wider">
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    Connected
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 text-xs font-bold uppercase tracking-wider">
                                    Not Connected
                                </span>
                            )}
                            {whatsappStatus?.sandbox && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-500 dark:text-orange-400 border border-orange-500/30 uppercase tracking-wide">
                                    Sandbox Mode
                                </span>
                            )}
                        </div>
                    </div>

                    <h3 className="text-lg font-bold text-darkTeal dark:text-light mb-1 text-left">WhatsApp Business</h3>
                    <p className="text-darkTeal/50 dark:text-light/50 text-sm mb-5 text-left">
                        {whatsappStatus?.connected
                            ? `Integration active • ${whatsappStatus.whatsappNumber || 'via Twilio'}`
                            : 'Not configured'
                        }
                    </p>

                    {whatsappStatus?.connected ? (
                        <>
                            <div className="space-y-3 mb-5 flex-1">
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Sent This Month</span>
                                    <span className="text-darkTeal dark:text-light font-medium">{whatsappStatus.monthlySent || 0}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm border-b border-teal/10 dark:border-light/5 pb-2">
                                    <span className="text-darkTeal/60 dark:text-light/60">Replies</span>
                                    <span className="text-darkTeal dark:text-light font-medium">{whatsappStatus.monthlyReplies || 0}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-darkTeal/60 dark:text-light/60">Response Rate</span>
                                    <span className="text-green-500 dark:text-green-400 font-medium">{whatsappStatus.responseRate || 0}%</span>
                                </div>
                            </div>

                            {whatsappStatus.sandbox && (
                                <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mb-4">
                                    <p className="text-xs text-orange-500 dark:text-orange-400">
                                        <strong>Sandbox Mode:</strong> Only pre-approved numbers can receive messages.
                                    </p>
                                </div>
                            )}

                            <div className="mt-auto">
                                <a
                                    href="mailto:support@offtrigger.com?subject=WhatsApp%20Business%20API%20Setup&body=Hi%20OffTrigger%20Team%2C%0A%0AI'd%20like%20to%20upgrade%20to%20the%20WhatsApp%20Business%20API.%20Please%20help%20me%20get%20started.%0A%0AThanks!"
                                    className="w-full py-2 rounded-lg bg-transparent hover:bg-primary/10 dark:hover:bg-cyan/10 text-primary dark:text-cyan border border-primary/50 dark:border-cyan/50 hover:border-primary dark:hover:border-cyan text-sm font-bold transition-all flex items-center justify-center gap-2"
                                >
                                    {whatsappStatus.sandbox ? 'Upgrade to Business API' : 'Configure'}
                                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                                </a>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col">
                            <div className="bg-mint dark:bg-light/5 border border-teal/10 dark:border-light/10 rounded-lg p-4 mb-4 flex-1">
                                <p className="text-sm text-darkTeal/70 dark:text-light/60 mb-3 font-semibold text-left">Get started with WhatsApp:</p>
                                <div className="space-y-3 text-left">
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 rounded-md bg-green-500/15 flex items-center justify-center shrink-0 mt-0.5">
                                            <span className="material-symbols-outlined text-green-500 text-sm">support_agent</span>
                                        </div>
                                        <div>
                                            <p className="text-sm text-darkTeal dark:text-light font-medium">Contact our team</p>
                                            <p className="text-xs text-darkTeal/50 dark:text-light/40">We'll set up the WhatsApp Business API for you — no technical knowledge needed.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 rounded-md bg-cyan/15 flex items-center justify-center shrink-0 mt-0.5">
                                            <span className="material-symbols-outlined text-cyan text-sm">integration_instructions</span>
                                        </div>
                                        <div>
                                            <p className="text-sm text-darkTeal dark:text-light font-medium">Bring your own API</p>
                                            <p className="text-xs text-darkTeal/50 dark:text-light/40">Already have a WhatsApp Business API via Twilio or Meta? Connect it directly.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <a
                                href="mailto:support@offtrigger.com?subject=WhatsApp%20Business%20API%20Setup&body=Hi%20OffTrigger%20Team%2C%0A%0AI'd%20like%20to%20set%20up%20WhatsApp%20Business%20API%20for%20my%20account.%20Please%20help%20me%20get%20started.%0A%0AThanks!"
                                className="w-full py-2.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-500/20"
                            >
                                <span className="material-symbols-outlined text-base">mail</span>
                                Contact Support to Set Up
                            </a>
                            <p className="text-[11px] text-center text-darkTeal/40 dark:text-light/30 mt-2">
                                Or email us directly at <a href="mailto:support@offtrigger.com" className="text-primary dark:text-cyan hover:underline">support@offtrigger.com</a>
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Coming Soon Section ─── */}
            <div className="mb-8">
                <div className="flex items-center gap-4 mb-6">
                    <h3 className="text-lg font-bold text-darkTeal dark:text-light text-left">Coming Soon</h3>
                    <div className="h-px flex-1 bg-gradient-to-r from-teal/20 dark:from-light/20 to-transparent"></div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                        { name: 'Lead Ads', icon: 'social_leaderboard', gradient: 'from-blue-500/10', circleBg: 'bg-blue-500/20', circleText: 'text-blue-500', circleBorder: 'border-blue-500/30', timeline: 'Q3 2026', dot: true },
                        { name: 'Instagram DM', icon: 'photo_camera', gradient: 'from-pink-500/10', circleBg: 'bg-pink-500/20', circleText: 'text-pink-500', circleBorder: 'border-pink-500/30', timeline: 'Q3 2026', dot: true },
                        { name: 'Slack Bot', icon: 'tag', gradient: 'from-purple-500/10', circleBg: 'bg-purple-500/20', circleText: 'text-purple-500', circleBorder: 'border-purple-500/30', timeline: 'Q4 2026', dot: false },
                        { name: 'Telegram', icon: 'send', gradient: 'from-sky-500/10', circleBg: 'bg-sky-500/20', circleText: 'text-sky-500', circleBorder: 'border-sky-500/30', timeline: 'Planned', dot: false },
                        { name: 'Intercom', icon: 'forum', gradient: 'from-indigo-500/10', circleBg: 'bg-indigo-500/20', circleText: 'text-indigo-500', circleBorder: 'border-indigo-500/30', timeline: 'Planned', dot: false },
                    ].map(channel => (
                        <div
                            key={channel.name}
                            className="bg-white dark:bg-navy border border-teal/10 dark:border-light/10 rounded-xl p-4 flex flex-col items-center justify-center text-center opacity-60 hover:opacity-100 transition-all cursor-not-allowed group relative overflow-hidden"
                        >
                            <div className={`absolute inset-0 bg-gradient-to-br ${channel.gradient} to-transparent pointer-events-none`}></div>
                            <div className={`w-10 h-10 rounded-full ${channel.circleBg} flex items-center justify-center ${channel.circleText} mb-3 border ${channel.circleBorder}`}>
                                <span className="material-symbols-outlined">{channel.icon}</span>
                            </div>
                            <p className="text-sm font-bold text-darkTeal dark:text-light mb-1">{channel.name}</p>
                            <span className="text-[10px] uppercase font-bold tracking-widest text-darkTeal/40 dark:text-light/40">{channel.timeline}</span>
                            {channel.dot && (
                                <div className="absolute top-2 right-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500 block"></span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8 mb-4 flex items-center justify-between text-xs text-darkTeal/30 dark:text-light/30">
                <p>© 2026 OffTrigger.</p>
                <div className="flex gap-4">
                    <a className="hover:text-primary dark:hover:text-cyan transition-colors" href="#">API Docs</a>
                    <a className="hover:text-primary dark:hover:text-cyan transition-colors" href="#">Support</a>
                </div>
            </div>

            {/* ─── SMS Usage Modal ─── */}
            {showUsageModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUsageModal(false)}>
                    <div
                        className="bg-white dark:bg-navy rounded-2xl border border-primary/20 dark:border-cyan/30 shadow-2xl dark:shadow-[0_0_40px_rgba(8,217,214,0.15)] w-full max-w-md p-6"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
                                    <span className="material-symbols-outlined text-xl text-indigo-500 dark:text-indigo-400">smartphone</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-darkTeal dark:text-light">SMS Usage</h3>
                                    <p className="text-xs text-darkTeal/50 dark:text-light/50 font-mono">{twilioStatus?.phoneNumber || 'N/A'}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowUsageModal(false)}
                                className="w-8 h-8 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 flex items-center justify-center text-darkTeal/60 dark:text-light/60 transition-colors"
                            >
                                <span className="material-symbols-outlined text-lg">close</span>
                            </button>
                        </div>

                        {/* Usage Bar */}
                        <div className="mb-6">
                            <div className="flex justify-between text-sm text-darkTeal/70 dark:text-light/70 mb-2">
                                <span>Monthly Quota</span>
                                <span className="font-bold text-primary dark:text-cyan">
                                    {twilioStatus?.monthlyUsage?.toLocaleString() || 0} / {(twilioStatus?.monthlyLimit || 1000).toLocaleString()}
                                </span>
                            </div>
                            <div className="h-3 w-full bg-teal/10 dark:bg-light/5 border border-teal/10 dark:border-light/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-indigo-500 to-primary dark:to-cyan rounded-full transition-all duration-700 ease-out"
                                    style={{ width: `${usagePercent}%` }}
                                ></div>
                            </div>
                            <p className="text-xs text-darkTeal/40 dark:text-light/40 mt-1">
                                {usagePercent}% of monthly limit used
                            </p>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <div className="bg-mint dark:bg-light/5 rounded-xl p-4 text-center border border-teal/10 dark:border-light/5">
                                <p className="text-[10px] text-darkTeal/40 dark:text-light/40 uppercase tracking-wider font-bold mb-1">Messages Sent</p>
                                <p className="text-2xl font-bold text-darkTeal dark:text-light">{twilioStatus?.sentCount || 0}</p>
                            </div>
                            <div className="bg-mint dark:bg-light/5 rounded-xl p-4 text-center border border-teal/10 dark:border-light/5">
                                <p className="text-[10px] text-darkTeal/40 dark:text-light/40 uppercase tracking-wider font-bold mb-1">Delivered</p>
                                <p className="text-2xl font-bold text-green-500 dark:text-green-400">{twilioStatus?.deliveredCount || 0}</p>
                            </div>
                            <div className="bg-mint dark:bg-light/5 rounded-xl p-4 text-center border border-teal/10 dark:border-light/5">
                                <p className="text-[10px] text-darkTeal/40 dark:text-light/40 uppercase tracking-wider font-bold mb-1">Response Rate</p>
                                <p className="text-2xl font-bold text-primary dark:text-cyan">{twilioStatus?.responseRate || 0}%</p>
                            </div>
                            <div className="bg-mint dark:bg-light/5 rounded-xl p-4 text-center border border-teal/10 dark:border-light/5">
                                <p className="text-[10px] text-darkTeal/40 dark:text-light/40 uppercase tracking-wider font-bold mb-1">Remaining</p>
                                <p className="text-2xl font-bold text-indigo-500 dark:text-indigo-400">
                                    {((twilioStatus?.monthlyLimit || 1000) - (twilioStatus?.monthlyUsage || 0)).toLocaleString()}
                                </p>
                            </div>
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={() => setShowUsageModal(false)}
                            className="w-full py-2.5 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 text-darkTeal dark:text-light border border-teal/20 dark:border-light/10 text-sm font-medium transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* ─── Limit Reached Modal ─── */}
            {showLimitModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-navy rounded-2xl border border-amber-500/30 shadow-2xl w-full max-w-md p-6 relative overflow-hidden">
                        {/* Background Effect */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-bl-full pointer-events-none"></div>

                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center border border-amber-500/20 mb-4 shadow-lg shadow-amber-500/10">
                                <span className="material-symbols-outlined text-4xl text-amber-500">lock</span>
                            </div>

                            <h3 className="text-xl font-bold text-darkTeal dark:text-light mb-2">Plan Limit Reached</h3>
                            <p className="text-darkTeal/60 dark:text-light/60 mb-6 text-sm leading-relaxed px-4">
                                {limitMessage}
                            </p>

                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setShowLimitModal(false)}
                                    className="flex-1 py-2.5 rounded-lg bg-teal/5 dark:bg-light/5 hover:bg-teal/10 dark:hover:bg-light/10 text-darkTeal dark:text-light font-bold text-sm transition-colors"
                                >
                                    Cancel
                                </button>
                                <a
                                    href="/billing"
                                    className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    Upgrade Now
                                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Channels;
